import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { bulkTradeApi } from '@/store/bulkTrade/api';
import type { TradeCaptureBulkTradeParams } from '@/store/bulkTrade/api/checkBulkTradeByTradeCapture.ts';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import { useAppDispatch } from '@/store/hooks.ts';

// ██████╗  █████╗ ████████╗ █████╗
// ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
// ██║  ██║███████║   ██║   ███████║
// ██║  ██║██╔══██║   ██║   ██╔══██║
// ██████╔╝██║  ██║   ██║   ██║  ██║
// ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

export type TradeCaptureError = {
  errorCode: string;
  errorMessage: string;
};

export type BulkTradeContextContent = {
  tabId: string;
  isChecking: boolean;
  tradeCaptureError: TradeCaptureError | undefined;
  checkBulkTradeWithTradeCapture: (params: Omit<TradeCaptureBulkTradeParams, 'tabId'>) => Promise<void>;
  resetTradeCaptureError: () => void;
};

export const BulkTradeContext = createContext<BulkTradeContextContent>({
  tabId: '',
  isChecking: false,
  tradeCaptureError: undefined,
  checkBulkTradeWithTradeCapture: async () => {},
  resetTradeCaptureError: () => {},
});

// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═══╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

export function BulkTradeProvider(props: { tabId: string; children: ReactNode }) {
  const { tabId, children } = props;

  const dispatch = useAppDispatch();

  const [isChecking, setIsChecking] = useState(false);
  const [tradeCaptureError, setTradeCaptureError] = useState<TradeCaptureError | undefined>(undefined);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one checkBulkTradeWithTradeCapture request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const checkBulkTradeWithTradeCapture = useCallback(
    async (params: Omit<TradeCaptureBulkTradeParams, 'tabId'>) => {
      // Add the call to the queue
      // If a request is already running, this one will wait until it completes
      return queue.add(async () => {
        // Timer to show "checking" state after 500ms
        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        try {
          await dispatch(bulkTradeApi.endpoints.checkBulkTradeByTradeCapture.initiate({ tabId, ...params })).unwrap();
        } catch (error: unknown) {
          const parsedError = parseTradeCaptureError(error);

          if (parsedError !== undefined) {
            setTradeCaptureError(parsedError);
          }
        } finally {
          // Clean up timer and state, even on error
          clearTimeout(checkTimer);
          setIsChecking(false);
        }
      });
    },
    [tabId, queue],
  );

  const resetTradeCaptureError = useCallback(() => {
    setTradeCaptureError(undefined);
    dispatch(bulkTradeSlice.actions.reset({ tabId }));
  }, [tabId]);

  const content: BulkTradeContextContent = useMemo(
    () => ({
      tabId,
      isChecking,
      tradeCaptureError,
      checkBulkTradeWithTradeCapture,
      resetTradeCaptureError,
    }),
    [tabId, isChecking, tradeCaptureError, checkBulkTradeWithTradeCapture, resetTradeCaptureError],
  );

  return <BulkTradeContext.Provider value={content}>{children}</BulkTradeContext.Provider>;
}

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function useBulkTradeContext() {
  return useContext(BulkTradeContext);
}

export function useBulkTradeIsChecking() {
  return useContext(BulkTradeContext).isChecking;
}

export function useCheckBulkTradeWithTradeCapture() {
  return useContext(BulkTradeContext).checkBulkTradeWithTradeCapture;
}

export function useBulkTradeTradeCaptureError() {
  return useContext(BulkTradeContext).tradeCaptureError;
}

export function useResetBulkTradeTradeCaptureError() {
  return useContext(BulkTradeContext).resetTradeCaptureError;
}

// ██╗  ██╗███████╗██╗     ██████╗ ███████╗██████╗ ███████╗
// ██║  ██║██╔════╝██║     ██╔══██╗██╔════╝██╔══██╗██╔════╝
// ███████║█████╗  ██║     ██████╔╝█████╗  ██████╔╝███████╗
// ██╔══██║██╔══╝  ██║     ██╔═══╝ ██╔══╝  ██╔══██╗╚════██║
// ██║  ██║███████╗███████╗██║     ███████╗██║  ██║███████║
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝╚══════╝

/**
 * Parses a trade capture HTTP 400 error from the unwrapped RTK Query rejection.
 *
 * Expected error shape:
 * { status: 400, data: { statusCode: number, reasonPhrase: string, content: string } }
 * where content is a JSON string: { title: string, status: number }
 */
function parseTradeCaptureError(error: unknown): TradeCaptureError | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const fetchError = error as { status?: unknown; data?: { content?: unknown } };

  if (fetchError.status !== 400) {
    return undefined;
  }

  const content = fetchError.data?.content;

  if (typeof content !== 'string') {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content) as { title?: unknown; status?: unknown };

    return {
      errorCode: String(parsed.status ?? 'UNKNOWN'),
      errorMessage: typeof parsed.title === 'string' ? parsed.title : 'An unexpected error occurred.',
    };
  } catch {
    return undefined;
  }
}
